import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import styles from './PersonalInformation.module.css';
import {
  addPersonalInformation,
  updatePersonalInformation,
  deletePersonalInformation,
  getPersonalInformation
} from '../../Fetch/fetchPersonalInformation';
import { Edit, Trash, Eye, EyeOff } from 'lucide-react';

export default function PersonalInformation({ buildingId = 1 }) {
  const location = useLocation();
  const isInitialLoad = useRef(true);
  const loadingRef = useRef(false);

  const [personalInfoList, setPersonalInfoList] = useState([]);
  const [filteredPersonalInfo, setFilteredPersonalInfo] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showNewForm, setShowNewForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeBuildingId, setActiveBuildingId] = useState(buildingId);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [newPersonalInfo, setNewPersonalInfo] = useState({
    Subject: "",
    LongDetail: "",
    UserName: "",
    Password: "",
    BuildingID: buildingId,
    UserID: null
  });
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [isMobile, setIsMobile] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  const [selectedListFormat, setSelectedListFormat] = useState(null); // 'numbered' or 'bulleted' or null
  const [listCounter, setListCounter] = useState(1);

  // CONSOLIDATED INITIALIZATION
  useEffect(() => {

    const initializeComponent = async () => {
      try {
        const checkMobile = () => setIsMobile(window.innerWidth <= 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);

        const userData = JSON.parse(localStorage.getItem('user'));
        const currentBuildingId = userData?.buildingId || buildingId;
        const userId = userData?.userId;
        
        setActiveBuildingId(currentBuildingId);
        setCurrentUserId(userId);
        setNewPersonalInfo(prev => ({ 
          ...prev, 
          BuildingID: currentBuildingId,
          UserID: userId 
        }));

        setTimeout(() => {
          loadPersonalInformation(currentBuildingId);
        }, 100);

        const handleBuildingChange = () => {
          const newUserData = JSON.parse(localStorage.getItem('user'));
          const newBuildingId = newUserData?.buildingId;
          const newUserId = newUserData?.userId;

          if (newBuildingId && newBuildingId !== currentBuildingId) {
            setActiveBuildingId(newBuildingId);
            setCurrentUserId(newUserId);
            setNewPersonalInfo(prev => ({ 
              ...prev, 
              BuildingID: newBuildingId,
              UserID: newUserId 
            }));
            loadPersonalInformation(newBuildingId);
          }
        };

        window.addEventListener('buildingChanged', handleBuildingChange);
        window.addEventListener('userDataChanged', handleBuildingChange);

        return () => {
          window.removeEventListener('resize', checkMobile);
          window.removeEventListener('buildingChanged', handleBuildingChange);
          window.removeEventListener('userDataChanged', handleBuildingChange);
        };

      } catch (error) {
        setError('שגיאה באתחול הרכיב');
      }
    };

    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      initializeComponent();
    }
  }, []);

  // FILTERING EFFECT
  useEffect(() => {

    let results = [...personalInfoList];

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      results = results.filter(item => {
        const subject = (item.Subject || item.subject || "").toString().toLowerCase();
        const longDetail = (item.LongDetail || item.longDetail || "").toString().toLowerCase();
        const userName = (item.UserName || item.userName || "").toString().toLowerCase();
        const id = (item.Id || item.id || "").toString().toLowerCase();

        return subject.includes(searchLower) ||
          longDetail.includes(searchLower) ||
          userName.includes(searchLower) ||
          id.includes(searchLower);
      });
    }

    setFilteredPersonalInfo(results);
    setCurrentPage(1);
  }, [personalInfoList, searchTerm]);

  // ESC KEY HANDLING
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    if (showNewForm || editingItem) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [showNewForm, editingItem]);

  // LOAD PERSONAL INFORMATION FUNCTION
  const loadPersonalInformation = async (buildingIdToUse = activeBuildingId) => {
    if (!buildingIdToUse) {
      return;
    }

    if (loadingRef.current) {
      return;
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      setError("");


      const response = await getPersonalInformation(buildingIdToUse);

      if (response.success) {
        const personalInfoData = response.personalInformation || [];
        setPersonalInfoList(personalInfoData);
        setError("");
      } else {
        const errorMsg = "שגיאה בטעינת המידע האישי: " + (response.message || "שגיאה לא ידועה");
        setError(errorMsg);
      }
    } catch (err) {
      const errorMsg = "שגיאה בטעינת המידע האישי: " + err.message;
      setError(errorMsg);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const itemData = editingItem ? editingItem : newPersonalInfo;
    const setItemData = editingItem ? setEditingItem : setNewPersonalInfo;

    setItemData({ ...itemData, [name]: value });
  };

  // Handle text formatting for LongDetail
  const handleTextFormatting = (formatType) => {
    // Toggle selection - if same format clicked, deselect it
    if (selectedListFormat === formatType) {
      setSelectedListFormat(null);
      setListCounter(1);
      return;
    }
    
    // Set new format and reset counter
    setSelectedListFormat(formatType);
    setListCounter(2); // Start at 2 because we'll insert 1. immediately
    
    const textareaRef = document.querySelector('textarea[name="LongDetail"]');
    if (!textareaRef) return;

    const start = textareaRef.selectionStart;
    const end = textareaRef.selectionEnd;
    const currentText = textareaRef.value;
    const selectedText = currentText.substring(start, end);

    let newText = '';
    let cursorPosition = start;

    if (formatType === 'numbered') {
      if (selectedText) {
        // If text is selected, convert each line to numbered list
        const lines = selectedText.split('\n');
        const numberedLines = lines.map((line, index) => {
          const trimmedLine = line.trim();
          if (trimmedLine) {
            return `${index + 1}. ${trimmedLine}`;
          }
          return line;
        });
        newText = currentText.substring(0, start) + numberedLines.join('\n') + currentText.substring(end);
        cursorPosition = start + numberedLines.join('\n').length;
      } else {
        // If no text selected, add numbered item at cursor position
        const beforeCursor = currentText.substring(0, start);
        const afterCursor = currentText.substring(start);
        
        // Check if we're at the beginning of a line or need a new line
        const needsNewLine = beforeCursor.length > 0 && !beforeCursor.endsWith('\n');
        const prefix = needsNewLine ? '\n' : '';
        
        // Use 1. for the first item when clicking the button
        const insertion = `${prefix}1. `;
        newText = beforeCursor + insertion + afterCursor;
        cursorPosition = start + insertion.length;
      }
    } else if (formatType === 'bulleted') {
      if (selectedText) {
        // If text is selected, convert each line to bulleted list
        const lines = selectedText.split('\n');
        const bulletedLines = lines.map(line => {
          const trimmedLine = line.trim();
          if (trimmedLine) {
            return `• ${trimmedLine}`;
          }
          return line;
        });
        newText = currentText.substring(0, start) + bulletedLines.join('\n') + currentText.substring(end);
        cursorPosition = start + bulletedLines.join('\n').length;
      } else {
        // If no text selected, add bullet point at cursor position
        const beforeCursor = currentText.substring(0, start);
        const afterCursor = currentText.substring(start);
        
        // Check if we're at the beginning of a line or need a new line
        const needsNewLine = beforeCursor.length > 0 && !beforeCursor.endsWith('\n');
        const prefix = needsNewLine ? '\n' : '';
        
        const insertion = `${prefix}• `;
        newText = beforeCursor + insertion + afterCursor;
        cursorPosition = start + insertion.length;
      }
    }

    // Update the state
    const itemData = editingItem ? editingItem : newPersonalInfo;
    const setItemData = editingItem ? setEditingItem : setNewPersonalInfo;
    setItemData({ ...itemData, LongDetail: newText });

    // Focus textarea and set cursor position
    setTimeout(() => {
      textareaRef.focus();
      textareaRef.setSelectionRange(cursorPosition, cursorPosition);
    }, 0);
  };

  // Handle Enter key in textarea for automatic list continuation
  const handleTextareaKeyDown = (e) => {
    if (e.key === 'Enter' && selectedListFormat) {
      e.preventDefault();
      
      const textarea = e.target;
      const start = textarea.selectionStart;
      const currentText = textarea.value;
      const beforeCursor = currentText.substring(0, start);
      const afterCursor = currentText.substring(start);
      
      let newLine = '\n';
      
      if (selectedListFormat === 'numbered') {
        newLine += `${listCounter}. `;
        setListCounter(listCounter + 1);
      } else if (selectedListFormat === 'bulleted') {
        newLine += '• ';
      }
      
      const newText = beforeCursor + newLine + afterCursor;
      
      // Update the state
      const itemData = editingItem ? editingItem : newPersonalInfo;
      const setItemData = editingItem ? setEditingItem : setNewPersonalInfo;
      setItemData({ ...itemData, LongDetail: newText });
      
      // Set cursor position after the new list item
      setTimeout(() => {
        textarea.focus();
        const newPosition = start + newLine.length;
        textarea.setSelectionRange(newPosition, newPosition);
      }, 0);
    }
  };

  // Format text for display (convert formatting to JSX)
  const formatTextForDisplay = (text) => {
    if (!text) return null;

    const lines = text.split('\n');
    const formattedElements = [];
    let currentList = null;
    let currentListType = null;

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      
      // Check for numbered list item (1. 2. 3. etc.)
      const numberedMatch = trimmedLine.match(/^(\d+)\.\s*(.+)$/);
      // Check for bullet point item
      const bulletMatch = trimmedLine.match(/^[•·]\s*(.+)$/);
      
      if (numberedMatch) {
        const [, number, content] = numberedMatch;
        
        // If we're not in a numbered list, start one
        if (currentListType !== 'numbered') {
          // Close previous list if exists
          if (currentList) {
            formattedElements.push(
              <ol key={`list-${formattedElements.length}`} className={styles.formattedNumberedList}>
                {currentList}
              </ol>
            );
          }
          currentList = [];
          currentListType = 'numbered';
        }
        
        currentList.push(
          <li key={`item-${index}`} className={styles.formattedListItem}>
            {content}
          </li>
        );
      } else if (bulletMatch) {
        const [, content] = bulletMatch;
        
        // If we're not in a bulleted list, start one
        if (currentListType !== 'bulleted') {
          // Close previous list if exists
          if (currentList) {
            if (currentListType === 'numbered') {
              formattedElements.push(
                <ol key={`list-${formattedElements.length}`} className={styles.formattedNumberedList}>
                  {currentList}
                </ol>
              );
            } else {
              formattedElements.push(
                <ul key={`list-${formattedElements.length}`} className={styles.formattedBulletList}>
                  {currentList}
                </ul>
              );
            }
          }
          currentList = [];
          currentListType = 'bulleted';
        }
        
        currentList.push(
          <li key={`item-${index}`} className={styles.formattedListItem}>
            {content}
          </li>
        );
      } else {
        // Close current list if we have one
        if (currentList && currentList.length > 0) {
          if (currentListType === 'numbered') {
            formattedElements.push(
              <ol key={`list-${formattedElements.length}`} className={styles.formattedNumberedList}>
                {currentList}
              </ol>
            );
          } else {
            formattedElements.push(
              <ul key={`list-${formattedElements.length}`} className={styles.formattedBulletList}>
                {currentList}
              </ul>
            );
          }
          currentList = null;
          currentListType = null;
        }
        
        // Add regular paragraph (if not empty)
        if (trimmedLine) {
          formattedElements.push(
            <p key={`para-${index}`} className={styles.formattedParagraph}>
              {trimmedLine}
            </p>
          );
        } else if (line === '') {
          // Add line break for empty lines
          formattedElements.push(<br key={`br-${index}`} />);
        }
      }
    });
    
    // Close any remaining list
    if (currentList && currentList.length > 0) {
      if (currentListType === 'numbered') {
        formattedElements.push(
          <ol key={`list-${formattedElements.length}`} className={styles.formattedNumberedList}>
            {currentList}
          </ol>
        );
      } else {
        formattedElements.push(
          <ul key={`list-${formattedElements.length}`} className={styles.formattedBulletList}>
            {currentList}
          </ul>
        );
      }
    }
    
    return formattedElements.length > 0 ? formattedElements : text;
  };

  // Toggle password visibility
  const togglePasswordVisibility = (itemId) => {
    setShowPasswords(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // Clear filters function
  const clearFilters = () => {
    setSearchTerm('');
  };

  // Close modal function
  const closeModal = () => {
    setShowNewForm(false);
    setEditingItem(null);
    setNewPersonalInfo({
      Subject: "",
      LongDetail: "",
      UserName: "",
      Password: "",
      BuildingID: activeBuildingId,
      UserID: currentUserId
    });
  };

  // Handle modal backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  // Validate personal information data
  const validatePersonalInfoData = (personalInfoData) => {
    if (!personalInfoData.Subject?.trim()) {
      return "נושא המידע האישי הוא שדה חובה";
    }
    if (personalInfoData.Subject?.length > 50) {
      return "נושא המידע האישי לא יכול להיות ארוך מ-50 תווים";
    }
    return null;
  };

  // Add new personal information
  const handleAddPersonalInfo = async () => {
    const validationError = validatePersonalInfoData(newPersonalInfo);
    if (validationError) {
      alert(validationError);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const personalInfoToAdd = {
        ...newPersonalInfo,
        BuildingID: activeBuildingId,
        UserID: currentUserId
      };

      const response = await addPersonalInformation(personalInfoToAdd);
      if (response.success) {
        await loadPersonalInformation(activeBuildingId);
        closeModal();
        alert("המידע האישי נוסף בהצלחה");
      } else {
        setError("שגיאה בהוספת המידע האישי: " + (response.message || "שגיאה לא ידועה"));
      }
    } catch (err) {
      setError("שגיאה בהוספת המידע האישי: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Update existing personal information
  const handleUpdatePersonalInfo = async () => {
    const validationError = validatePersonalInfoData(editingItem);
    if (validationError) {
      alert(validationError);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await updatePersonalInformation(editingItem);
      if (response.success) {
        await loadPersonalInformation(activeBuildingId);
        closeModal();
        alert("המידע האישי עודכן בהצלחה");
      } else {
        setError("שגיאה בעדכון המידע האישי: " + (response.message || "שגיאה לא ידועה"));
      }
    } catch (err) {
      setError("שגיאה בעדכון המידע האישי: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Delete personal information
  const handleDeletePersonalInfo = async (id) => {
    if (!window.confirm("האם אתה בטוח שברצונך למחוק את המידע האישי?")) {
      return;
    }

    try {
      setLoading(true);
      setError("");
      const response = await deletePersonalInformation({ Id: id });
      if (response.success) {
        await loadPersonalInformation(activeBuildingId);
        alert("המידע האישי נמחק בהצלחה");
      } else {
        setError("שגיאה במחיקת המידע האישי: " + (response.message || "שגיאה לא ידועה"));
      }
    } catch (err) {
      setError("שגיאה במחיקת המידע האישי: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Start editing personal information
  const startEditPersonalInfo = (item) => {
    const itemWithCorrectCase = {
      Id: item.Id || item.id,
      Subject: item.Subject || item.subject || "",
      LongDetail: item.LongDetail || item.longDetail || "",
      UserName: item.UserName || item.userName || "",
      Password: item.Password || item.password || "",
      BuildingID: item.BuildingID || item.buildingID || activeBuildingId,
      UserID: item.UserID || item.userID || currentUserId
    };

    setEditingItem(itemWithCorrectCase);
    setShowNewForm(false);
  };

  // Sort personal information
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });

    setFilteredPersonalInfo(prevItems => {
      return [...prevItems].sort((a, b) => {
        const aValue = a[key] || a[key.toLowerCase()] || "";
        const bValue = b[key] || b[key.toLowerCase()] || "";

        if (aValue < bValue) {
          return direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    });
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredPersonalInfo.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPersonalInfo.slice(indexOfFirstItem, indexOfLastItem);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      return new Date(dateString).toLocaleDateString('he-IL');
    } catch {
      return dateString;
    }
  };

  // Action Menu Modal Component
  const ActionMenu = ({ item, onEdit, onDelete, loading }) => {
    const [isOpen, setIsOpen] = useState(false);

    const actions = [
      {
        label: '✏️ ערוך',
        onClick: () => onEdit(item),
        disabled: loading
      },
      {
        label: '🗑️ מחק',
        onClick: () => onDelete(item.Id || item.id),
        disabled: loading,
        isDanger: true
      }
    ];

    const handleActionClick = (action) => {
      if (!action.disabled) {
        action.onClick();
        setIsOpen(false);
      }
    };

    return (
      <>
        <button
          className={`${styles.actionMenuTrigger} ${loading ? styles.disabled : ''}`}
          onClick={() => setIsOpen(true)}
          disabled={loading}
          aria-label="פתח תפריט פעולות"
        >
          <span>⋯</span>
        </button>

        {isOpen && createPortal(
          <div className={styles.modalOverlay} onClick={() => setIsOpen(false)}>
            <div className={styles.actionMenuModal} onClick={(e) => e.stopPropagation()}>
              <div className={styles.actionMenuHeader}>
                <h3>פעולות</h3>
                <button
                  className={styles.closeButton}
                  onClick={() => setIsOpen(false)}
                  aria-label="סגור"
                >
                  ✕
                </button>
              </div>
              <div className={styles.actionMenuContent}>
                {actions.map((action, index) => (
                  <button
                    key={index}
                    className={`${styles.actionMenuItem} ${action.isDanger ? styles.dangerAction : ''} ${action.disabled ? styles.disabled : ''}`}
                    onClick={() => handleActionClick(action)}
                    disabled={action.disabled}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          </div>,
          document.body
        )}
      </>
    );
  };

  // Statistics
  const totalItems = personalInfoList.length;

  // Render personal information cards
  const renderPersonalInfoCards = () => {
    if (currentItems.length === 0) {
      return (
        <div className={styles.emptyState}>
          {searchTerm ? 'לא נמצא מידע אישי התואם לחיפוש' : 'אין מידע אישי'}
        </div>
      );
    }

    return (
      <div className={styles.cardsContainer}>
        <div className={styles.cardsGrid}>
          {currentItems.map((item, index) => (
            <div key={item.Id || item.id || `item-${index}`} className={styles.dataCard}>
              <div className={styles.cardHeader}>
                <div className={styles.cardTitle}>
                  <span className={styles.cardIcon}>#</span>
                  <span>{item.Subject || item.subject}</span>
                </div>
                <div className={styles.cardHeaderActions}>
                  <ActionMenu
                    item={item}
                    onEdit={startEditPersonalInfo}
                    onDelete={handleDeletePersonalInfo}
                    loading={loading}
                  />
                </div>
              </div>

              <div className={styles.cardBody}>
                <div className={styles.cardInfo}>
                  <span className={styles.infoLabel}>תאריך יצירה:</span>
                  <span className={styles.infoValue}>{formatDate(item.CreateDate || item.createDate)}</span>
                </div>

                {(item.LongDetail || item.longDetail) && (
                  <div className={styles.cardInfo}>
                    <span className={styles.infoLabel}>פרטים:</span>
                    <div className={styles.infoValue}>
                      <div className={styles.formattedContent}>
                        {formatTextForDisplay(item.LongDetail || item.longDetail)}
                      </div>
                    </div>
                  </div>
                )}

                {(item.UserName || item.userName) && (
                  <div className={styles.cardInfo}>
                    <span className={styles.infoLabel}>שם משתמש:</span>
                    <span className={styles.infoValue}>{item.UserName || item.userName}</span>
                  </div>
                )}

                {(item.Password || item.password) && (
                  <div className={styles.cardInfo}>
                    <span className={styles.infoLabel}>סיסמה:</span>
                    <div className={styles.passwordContainer}>
                      <span className={styles.infoValue}>
                        {showPasswords[item.Id || item.id] 
                          ? (item.Password || item.password) 
                          : '•'.repeat((item.Password || item.password || '').length)
                        }
                      </span>
                      <button
                        className={styles.passwordToggle}
                        onClick={() => togglePasswordVisibility(item.Id || item.id)}
                        title={showPasswords[item.Id || item.id] ? 'הסתר סיסמה' : 'הצג סיסמה'}
                      >
                        {showPasswords[item.Id || item.id] ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render modal content
  const renderModal = () => {
    if (!showNewForm && !editingItem) return null;

    return (
      <div className={styles.modalOverlay} onClick={handleBackdropClick}>
        <div className={styles.modalContent}>
          {/* Modal Header */}
          <div className={styles.modalHeader}>
            <h3>{editingItem ? "עריכת מידע אישי" : "הוספת מידע אישי חדש"}</h3>
            <button className={styles.modalCloseButton} onClick={closeModal} type="button">
              ✕
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}

          {/* Modal Body */}
          <div className={styles.modalBody}>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label>נושא: *</label>
                <input
                  type="text"
                  name="Subject"
                  value={editingItem ? editingItem.Subject || "" : newPersonalInfo.Subject}
                  onChange={handleInputChange}
                  placeholder="הכנס נושא (עד 50 תווים)"
                  maxLength={50}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label>פרטים נוספים:</label>
                <div className={styles.textareaContainer}>
                  <div className={styles.formattingButtons}>
                    <button
                      type="button"
                      className={`${styles.formatButton} ${selectedListFormat === 'numbered' ? styles.formatButtonActive : ''}`}
                      onClick={() => handleTextFormatting('numbered')}
                      title="הוסף רשימה ממוספרת"
                    >
                      <span className={styles.formatIcon}>1.</span>
                      רשימה ממוספרת
                    </button>
                    <button
                      type="button"
                      className={`${styles.formatButton} ${selectedListFormat === 'bulleted' ? styles.formatButtonActive : ''}`}
                      onClick={() => handleTextFormatting('bulleted')}
                      title="הוסף רשימה עם תבליטים"
                    >
                      <span className={styles.formatIcon}>•</span>
                      רשימה מנוקדת
                    </button>
                  </div>
                  <textarea
                    name="LongDetail"
                    value={editingItem ? editingItem.LongDetail || "" : newPersonalInfo.LongDetail}
                    onChange={handleInputChange}
                    onKeyDown={handleTextareaKeyDown}
                    placeholder="הכנס פרטים נוספים&#10;&#10;השתמש בכפתורי העיצוב למעלה ליצירת רשימות"
                    rows={6}
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>שם משתמש:</label>
                <input
                  type="text"
                  name="UserName"
                  value={editingItem ? editingItem.UserName || "" : newPersonalInfo.UserName}
                  onChange={handleInputChange}
                  placeholder="הכנס שם משתמש"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
              </div>

              <div className={styles.formGroup}>
                <label>סיסמה:</label>
                <input
                  type="password"
                  name="Password"
                  value={editingItem ? editingItem.Password || "" : newPersonalInfo.Password}
                  onChange={handleInputChange}
                  placeholder="הכנס סיסמה"
                  autoComplete="new-password"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className={styles.modalFooter}>
            <button
              className={styles.saveButton}
              onClick={editingItem ? handleUpdatePersonalInfo : handleAddPersonalInfo}
              disabled={loading}
              type="button"
            >
              {loading ? "שומר..." : (editingItem ? "עדכן מידע" : "הוסף מידע")}
            </button>

            <button
              className={styles.cancelButton}
              onClick={closeModal}
              disabled={loading}
              type="button"
            >
              ביטול
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.personalInformationContainer}>
      {/* Header with Statistics */}
      <div className={styles.header}>
        <h1>ניהול מידע אישי</h1>
        <div className={styles.stats}>
          <div className={styles.statCard}>
            <div>
              <h3>סה"כ פריטים</h3>
              <p className={styles.statValue}>{totalItems}</p>
            </div>
          </div>
        </div>
      </div>

      {error && !showNewForm && !editingItem && (
        <div className={styles.errorMessage}>
          {error}
        </div>
      )}

      {/* Actions and Filters */}
      <div className={styles.actions}>
        <div className={styles.filtersContainer}>
          <div className={styles.searchBar}>
            <input
              type="text"
              placeholder="חיפוש מידע אישי..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {searchTerm && (
            <button
              className={styles.clearFiltersButton}
              onClick={clearFilters}
              title="נקה מסננים"
            >
              נקה
            </button>
          )}
        </div>

        <button
          className={styles.addButton}
          onClick={() => {
            setShowNewForm(true);
          }}
          disabled={loading}
        >
          מידע אישי חדש +
        </button>
      </div>

      {/* Mobile Sort Options */}
      {isMobile && (
        <div className={styles.mobileSortContainer}>
          <label>מיון לפי:</label>
          <select
            onChange={(e) => requestSort(e.target.value)}
            className={styles.sortSelect}
          >
            <option value="">בחר מיון</option>
            <option value="Subject">נושא</option>
            <option value="CreateDate">תאריך יצירה</option>
          </select>
        </div>
      )}

      {/* Personal Information Display */}
      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>טוען מידע אישי...</p>
        </div>
      ) : (
        renderPersonalInfoCards()
      )}

      {/* Pagination */}
      {filteredPersonalInfo.length > 0 && (
        <div className={styles.pagination}>
          <div className={styles.paginationControls}>
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1 || loading}
              className={styles.paginationButton}
            >
              הקודם
            </button>
            <span className={styles.pageInfo}>
              עמוד {currentPage} מתוך {totalPages}
            </span>
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages || loading}
              className={styles.paginationButton}
            >
              הבא
            </button>
          </div>

          <div className={styles.resultsPerPage}>
            <span>פריטים בעמוד:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              disabled={loading}
            >
              <option value={6}>6</option>
              <option value={12}>12</option>
              <option value={24}>24</option>
            </select>
          </div>

          <div className={styles.totalInfo}>
            מציג {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredPersonalInfo.length)} מתוך {filteredPersonalInfo.length} פריטים
          </div>
        </div>
      )}

      {/* Modal */}
      {renderModal()}
    </div>
  );
}